import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { SelectField } from '../../components/ui/SelectField';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Badge } from '../../components/ui/badge';
import { MultiSelectFilter } from '../../components/ui/MultiSelectFilter';
import { resolvePortalReportConfig } from '../../lib/portalReport/config';
import { portalThemeStyle } from '../../lib/portalReport/themeVars';
import { PORTAL_COMPARISON_MODES } from '../../lib/portalReport/comparison';
import { presetToRange } from '../../lib/reportingDateRange';
import { portalRangeFor, RESTYLE_PRESETS } from '../../lib/portalReport/reportPeriod';
import {
  RestyledReport,
  type ReportTab,
  readRestyleFlag,
  writeRestyleFlag,
} from './portal/report/restyle';
import type {
  MetaGoalBucket,
  PortalComparisonMode,
  PortalGoogleReport,
  PortalGrouping,
  PortalMetaReport,
  PortalOverviewReport,
} from '../../types/portalReport';
import type { PortalWebsiteReport } from '../../types/portalWebsite';
import type { ReportingDatePreset } from '../../types/reporting';
import {
  REPORT_TEMPLATES,
  REPORT_TEMPLATE_LABELS,
  type ReportTemplate,
} from '../../types/reportTemplate';
import { listAccounts } from '../accounts';
import { ChannelBlock } from './portal/report/ChannelBlock';
import { PORTAL_PRESETS } from './portal/report/PeriodControl';
import { ReportSections, type SectionRenderers } from './portal/report/ReportSections';
import {
  ByLocationTable,
  CampaignsTable,
  CreativesGrid,
  SearchTermsTable,
} from './portal/report/tables';
import {
  fetchClientPreviewGoogle,
  fetchClientPreviewMeta,
  fetchClientPreviewOverview,
  fetchClientPreviewWebsite,
} from './previewApi';

// CLIENT PORTAL round 2, item 7 — the STAFF "view as client" page.
//
// ⚠️⚠️ THE SAME REPORT COMPONENTS, A DIFFERENT DATA PATH. ChannelBlock, the
// tables and ReportSections are imported directly from the portal's report
// folder, so what an AM sees here IS what the client sees — not a staff
// re-implementation that could drift. What is NOT shared is the fetching: this
// page calls /api/client-preview/* behind the staff guard, never /api/portal/*.
// A source-shape test pins that it does not import the portal's own fetcher.
//
// ⚠️ It renders inside the STAFF shell (AppShell, via the route) and is labelled
// plainly as a preview, naming the account and period — an AM must never be
// unsure whether they are looking at their own view or the client's.

type Tab = ReportTab;

/** The three report shapes this page can be showing. */
type PreviewReport = PortalOverviewReport | PortalMetaReport | PortalGoogleReport | PortalWebsiteReport;

/** ⚠️ Website Analytics exists only on the restyled report; there is no legacy version. */
function isWebsiteReport(r: PreviewReport): r is PortalWebsiteReport {
  return 'audience' in r;
}

export function ClientReportPreview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const accountId = searchParams.get('accountId') ?? '';
  const [tab, setTab] = useState<Tab>('overview');
  // ⚠️⚠️ THE FLAG. Staff-only, per browser, and DEFAULT ON since 2026-09-01 —
  // it selects the client's live report, and only an explicit opt-out shows the
  // retired one. Read LAZILY (not in an effect), or the page would paint one
  // report and swap.
  const [restyle, setRestyle] = useState<boolean>(() =>
    readRestyleFlag(typeof window === 'undefined' ? null : window.localStorage),
  );
  // ⚠️ The restyle opens on ITS OWN default period — this month to date,
  // EXCLUDING today — while the retired preview keeps last month. Two
  // presentations, two defaults, neither guessing on behalf of the other.
  //
  // ⚠️⚠️ IT READS THE SAME `readRestyleFlag` THE LAYOUT DOES, in its own lazy
  // initialiser, so the initial PERIOD and the initial LAYOUT always come from
  // the same branch. Seeding this from anything else — or from a hardcoded
  // default — would open the new layout on the old report's period, which is
  // the subtle half of this defect rather than the obvious one.
  const [preset, setPreset] = useState<ReportingDatePreset>(
    readRestyleFlag(typeof window === 'undefined' ? null : window.localStorage)
      ? 'this_month'
      : 'last_month',
  );
  const [grouping, setGrouping] = useState<PortalGrouping>('daily');
  const [comparison, setComparison] = useState<PortalComparisonMode>('previous');
  const [campaignIds, setCampaignIds] = useState<string[] | undefined>(undefined);
  // ⚠️ The goal-split Meta page's goal filter, with the portal's semantics
  // exactly (useReportState): `undefined` is "All Campaigns", not a bucket.
  const [goalBucket, setGoalBucket] = useState<MetaGoalBucket | undefined>(undefined);
  // Website Analytics' studio picker. Only sent on that tab.
  const [studio, setStudio] = useState<string | null>(null);
  // ⚠️ "PREVIEW AS" (2026-11-11): see an account under another template before
  // setting it. STATE ONLY — this is a request parameter, and nothing on this page
  // writes it to the account.
  const [previewTemplate, setPreviewTemplate] = useState<ReportTemplate | 'own'>('own');

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() });
  const accounts = accountsQuery.data ?? [];
  const account = accounts.find((a) => a.id === accountId) ?? null;

  // ⚠️ The restyle NARROWS the shared resolver to exclude today; the existing
  // preview is untouched and still uses `presetToRange` directly.
  const range = restyle
    ? portalRangeFor(preset === 'custom' ? 'this_month' : preset, new Date())
    : presetToRange(preset === 'custom' ? 'last_month' : preset, new Date());
  const today = new Date().toISOString().slice(0, 10);
  const params = accountId
    ? // ⚠️ `goalBucket` MUST be in here: `params` is the query key, so this is
      // what makes a filter click refetch.
      {
        accountId,
        from: range.from,
        to: range.to,
        grouping,
        today,
        comparison,
        campaignIds,
        goalBucket,
        ...(previewTemplate !== 'own' ? { template: previewTemplate } : {}),
      }
    : null;

  const report = useQuery<PreviewReport>({
    queryKey: ['client-preview', tab, params, tab === 'website' ? studio : null],
    queryFn: async (): Promise<PreviewReport> => {
      if (tab === 'meta') return fetchClientPreviewMeta(params!);
      if (tab === 'google') return fetchClientPreviewGoogle(params!);
      if (tab === 'website') return fetchClientPreviewWebsite({ ...params!, studio });
      return fetchClientPreviewOverview(params!);
    },
    enabled: params !== null,
    retry: false,
  });

  // ⚠️ The brand comes from the PAYLOAD, exactly as it does in the client's own
  // report — so an AM previewing a Snap account sees the Snap-themed report the
  // client sees, not a kyck-default approximation of it.
  const config = resolvePortalReportConfig(
    report.data?.brandAccountId ?? null,
    report.data?.brandName ?? null,
  );

  return (
    <div>
      <PageHeader
        title="Client report preview"
        description="Exactly what a client sees in their portal, for the account and period you choose."
      />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div className="min-w-[16rem]">
          <label className="mb-1 block text-sm font-medium text-slate-700">Account</label>
          <Combobox
            aria-label="Account"
            placeholder={accountsQuery.isLoading ? 'Loading…' : 'Choose an account…'}
            searchPlaceholder="Search accounts…"
            value={accountId}
            onValueChange={(id) => setSearchParams(id ? { accountId: id } : {})}
            options={accounts.map((a) => ({
              value: a.id,
              label: a.name,
              description: a.accountType,
            }))}
          />
        </div>
        <SelectField
          aria-label="Reporting period"
          value={preset}
          onValueChange={(v) => setPreset(v as ReportingDatePreset)}
          options={(restyle ? RESTYLE_PRESETS : PORTAL_PRESETS)
            .filter((p) => p.value !== 'custom')
            .map((p) => ({ value: p.value, label: p.label }))}
        />
        <SelectField
          aria-label="Compare with"
          value={comparison}
          onValueChange={(v) => setComparison(v as PortalComparisonMode)}
          options={PORTAL_COMPARISON_MODES.map((m) => ({
            value: m.value,
            label: `vs ${m.label.toLowerCase()}`,
          }))}
        />
        <SelectField
          aria-label="Preview as template"
          value={previewTemplate}
          onValueChange={(v) => setPreviewTemplate(v as ReportTemplate | 'own')}
          options={[
            { value: 'own', label: "Preview as: the account's own template" },
            ...REPORT_TEMPLATES.map((t) => ({ value: t, label: `Preview as: ${REPORT_TEMPLATE_LABELS[t]}` })),
          ]}
        />
        {/* ⚠️⚠️ THE FLAG, AND IT IS STAFF-ONLY. It now DEFAULTS TO THE CLIENT'S
            LIVE REPORT — Phase 1 (2026-09-01) made every portal user see the
            restyle, and a preview that opened on the retired report was showing
            an AM something no client could see.

            ⚠️ THE TWO LABELS NAME WHICH REPORT IS REAL, rather than framing the
            restyle as something to "try": it is the live one, and the
            alternative is retired. An AM must be able to tell from the button
            alone which view they are looking at. */}
        <Button
          variant={restyle ? 'default' : 'outline'}
          aria-pressed={restyle}
          onClick={() => {
            const next = !restyle;
            setRestyle(next);
            writeRestyleFlag(typeof window === 'undefined' ? null : window.localStorage, next);
            // ⚠️ The two presentations have DIFFERENT defaults (the restyle
            // excludes today), so switching moves the period to the one the
            // chosen presentation opens on rather than leaving a stale value
            // that belongs to the other.
            setPreset(next ? 'this_month' : 'last_month');
          }}
        >
          {restyle ? "Client's live report" : 'Retired report — not what clients see'}
        </Button>
      </div>

      {!accountId ? (
        <p className="text-sm text-slate-600">
          Choose an account to preview the report their portal users see.
        </p>
      ) : (
        <>
          {/* ⚠️ Says plainly WHOSE view this is and over WHAT period. An AM
              looking at figures needs to know at a glance that they are seeing
              the client's version, not an internal one. */}
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
            <Badge variant="secondary">Client view</Badge>
            <span className="text-amber-900">
              Showing <span className="font-medium">{account?.name ?? accountId}</span> for{' '}
              {range.from} to {range.to}, as their portal users see it.
            </span>
          </div>

          {/* ⚠️ The SAME token scope the portal shell carries (src/index.css).
              The report components are shared, so without it an AM's preview
              would render in different ink from the client's own report — the
              drift the one-renderer rule exists to prevent. Scoped to the report
              body rather than the whole page, so the staff chrome above it
              (PageHeader, the pickers) keeps the staff palette. */}
          {/* ⚠️ The brand theme too, not just the token scope — "exactly what a
              client sees" has to include the branding, or an AM previewing a
              Snap account would see a kyck-default report the client never
              gets. Same resolver, same payload field. */}
          {/* ⚠️⚠️ THE RESTYLE IS A SEPARATE BRANCH, NOT A VARIANT OF THIS ONE.
              It carries its own `.kyck-portal` root (with the display-mode
              attribute on it), so the existing markup below is reached only
              with the flag off and is byte-identical to what it was. */}
          {restyle ? (
            report.isLoading ? (
              <CenteredSpinner />
            ) : report.error || !report.data ? (
              <PreviewError error={report.error} onRetry={() => void report.refetch()} />
            ) : (
              <RestyledReport
                tab={tab}
                onTabChange={setTab}
                // ⚠️ Without this the goal filter buttons never render on the
                // preview — in the audience AND the creatives headings — while
                // working on the client portal. Harmless on a non-goal-split
                // account: the control also needs a goal report in the payload.
                goalFilter={{ value: goalBucket ?? null, onChange: setGoalBucket }}
                // ⚠️⚠️ THE EXISTING `MultiSelectFilter`, PASSED IN. The brief
                // is explicit that the channel pages keep this control
                // driven by `appliedCampaignIds` — Scott asked for the same
                // filter, not a new one. Rebuilding it inside the restyle
                // would be a second control with a second idea of what an
                // empty selection means, which is the reversal round 2 already
                // made once. Supplying it from the host also keeps the report
                // free of a shadcn import.
                controls={
                  'campaignOptions' in report.data &&
                  report.data.campaignOptions.length > 0 ? (
                    <MultiSelectFilter
                      label="Campaigns"
                      ariaLabel="Filter by campaign"
                      placeholder="All campaigns"
                      options={report.data.campaignOptions.map((o) => ({
                        value: o.campaignId,
                        label: o.campaignName,
                      }))}
                      selected={campaignIds ?? []}
                      // ⚠️ EMPTY MEANS NO CONSTRAINT here, matching this
                      // control’s own trigger ("All campaigns"). The SERVER’s
                      // empty-vs-absent distinction is untouched: `[]` still
                      // means "filtered to nothing" on the wire, which is what
                      // stops a tampered empty filter reading as "show me
                      // everything".
                      onChange={(next) =>
                        setCampaignIds(next.length === 0 ? undefined : next)
                      }
                    />
                  ) : null
                }
                overview={'combined' in report.data ? report.data : null}
                meta={
                  'creatives' in report.data ? (report.data as PortalMetaReport) : null
                }
                google={
                  'searchTerms' in report.data ? (report.data as PortalGoogleReport) : null
                }
                website={isWebsiteReport(report.data) ? report.data : null}
                onStudioChange={setStudio}
                config={config}
                today={today}
              />
            )
          ) : (
          <div className="kyck-portal" style={portalThemeStyle(config.theme)}>
            <nav className="mb-4 flex gap-1 border-b border-border" aria-label="Report sections">
            {(['overview', 'google', 'meta'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                aria-pressed={tab === t}
                className={
                  tab === t
                    ? '-mb-px border-b-2 border-slate-900 px-3 py-2 text-sm font-medium text-foreground'
                    : '-mb-px border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-slate-800'
                }
              >
                {t === 'overview' ? 'Overview' : t === 'google' ? 'Google Ads' : 'Meta'}
              </button>
            ))}
          </nav>

          {report.isLoading ? (
            <CenteredSpinner />
          ) : report.error || !report.data ? (
            <PreviewError error={report.error} onRetry={() => void report.refetch()} />
          ) : isWebsiteReport(report.data) ? (
            <p className="text-sm text-muted-foreground">
              Website Analytics is only on the new report layout.
            </p>
          ) : (
            <PreviewBody
              tab={tab}
              report={report.data}
              config={config}
              grouping={grouping}
              onGroupingChange={setGrouping}
              campaignIds={campaignIds}
              onCampaignIdsChange={setCampaignIds}
            />
          )}
          </div>
          )}
        </>
      )}
    </div>
  );
}

function PreviewError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="rounded-md border border-border bg-card p-6 text-center">
      <p className="text-sm text-slate-600">
        Couldn&apos;t load this preview: {(error as Error)?.message ?? 'unknown error'}
      </p>
      <Button variant="outline" className="mt-4" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function PreviewBody({
  tab,
  report,
  config,
  grouping,
  onGroupingChange,
  campaignIds,
  onCampaignIdsChange,
}: {
  tab: Tab;
  /** ⚠️ Never a website report — the legacy layout has no website page. */
  report: Exclude<PreviewReport, PortalWebsiteReport>;
  config: ReturnType<typeof resolvePortalReportConfig>;
  grouping: PortalGrouping;
  onGroupingChange: (g: PortalGrouping) => void;
  campaignIds: string[] | undefined;
  onCampaignIdsChange: (ids: string[] | undefined) => void;
}) {
  // The Overview carries three blocks; a channel page carries one. Narrowed by
  // the presence of `combined` rather than by the tab, so the render follows the
  // payload it actually received.
  const overview = 'combined' in report ? report : null;
  const channel = 'block' in report ? report : null;
  const meta = channel && 'creatives' in channel ? channel : null;
  const google = channel && 'searchTerms' in channel ? channel : null;

  const blockFor = (block: Parameters<typeof ChannelBlock>[0]['block'] | undefined) =>
    block
      ? () => (
          <ChannelBlock
            block={block}
            labels={report.labels}
            grouping={grouping}
            onGroupingChange={onGroupingChange}
          />
        )
      : undefined;

  const renderers: SectionRenderers = {
    combined: blockFor(overview?.combined),
    google: blockFor(overview ? overview.google : google?.block),
    meta: blockFor(overview ? overview.meta : meta?.block),
    byLocation: () =>
      report.byLocation ? (
        <ByLocationTable rows={report.byLocation} labels={report.labels} />
      ) : null,
    campaigns: () =>
      channel ? <CampaignsTable rows={channel.campaigns} labels={report.labels} /> : null,
    creatives: () =>
      meta ? <CreativesGrid rows={meta.creatives} labels={report.labels} /> : null,
    searchTerms: () =>
      google ? (
        <SearchTermsTable
          rows={google.searchTerms}
          labels={report.labels}
          capped={google.searchTermsCapped}
          total={google.searchTermsTotal}
        />
      ) : null,
  };

  const order =
    tab === 'overview'
      ? config.overviewSections
      : tab === 'meta'
        ? config.metaSections
        : config.googleSections;

  return (
    <>
      {channel && channel.campaignOptions.length > 0 && (
        <div className="mb-4">
          <MultiSelectFilter
            label="Campaigns"
            ariaLabel="Filter by campaign"
            placeholder="All campaigns"
            options={channel.campaignOptions.map((o) => ({
              value: o.campaignId,
              label: o.campaignName,
            }))}
            selected={campaignIds ?? []}
            onChange={(next) => onCampaignIdsChange(next.length === 0 ? undefined : next)}
          />
        </div>
      )}
      <ReportSections order={order} renderers={renderers} />
    </>
  );
}
